import { format, isToday, isYesterday, addDays, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { ja } from 'date-fns/locale';

/**
 * Converts total minutes to a string representation of hours and minutes.
 * @param {number} totalMinutes - The total minutes to convert.
 *   @param {boolean} compact - If true, returns "Xh Ym" format. Otherwise, "X時間 Y分". Defaults to false.
 * @returns {string} Formatted time string.
 */
export const toHoursMinutes = (totalMinutes, compact = false) => {
  if (isNaN(totalMinutes) || totalMinutes < 0) return compact ? '0m' : '0分';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  
  if (compact) {
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  if (h === 0) return `${m}分`;
  if (m === 0) return `${h}時間`;
  return `${h}時間 ${m}分`;
};

/**
 * Formats a date string for use as a header.
 * @param {string} dateStr - The date string (e.g., "yyyy-MM-dd").
 * @returns {string} Formatted date header (e.g., "Today", "Yesterday", "M月d日 (E)").
 */
export const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'M月d日 (E)', { locale: ja });
};

/**
 * Calculates the consecutive study streak from a list of records.
 * @param {Array<object>} records - Array of record objects, each with a 'timestamp' (Date object).
 * @returns {number} The current streak in days.
 */
export const calculateStreak = (records) => {
  if (!records || records.length === 0) return 0;

  // totalMinutes > 0 の記録のみを対象にし、タイムスタンプで降順にソート
  const sortedRecords = records
    .filter(r => r.totalMinutes > 0)
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  if (sortedRecords.length === 0) return 0;

  const latestDate = sortedRecords[0].timestamp;

  // 最新の学習が今日でも昨日でもなければ、ストリークは0
  if (!isToday(latestDate) && !isYesterday(latestDate)) {
    return 0;
  }

  let streak = 1;
  let lastDate = latestDate;

  for (let i = 1; i < sortedRecords.length; i++) {
    const currentDate = sortedRecords[i].timestamp;

    // 同じ日の記録はスキップ
    if (isSameDay(currentDate, lastDate)) {
      continue;
    }

    const expectedDate = addDays(lastDate, -1);
    if (isSameDay(currentDate, expectedDate)) {
      streak++;
      lastDate = currentDate;
    } else {
      // 連続が途切れたら終了
      break;
    }
  }

  return streak;
};

/**
 * Generates a unique week identifier string (e.g., "2023-W45") for a given date.
 * Uses ISO 8601 week date system where Monday is the first day of the week.
 * @param {Date} date - The date for which to get the week ID.
 * @returns {string} The week ID string.
 */
export const getWeekId = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

/**
 * Converts a string to a consistent HSL color.
 * @param {string} str - The input string.
 * @returns {string} The HSL color string.
 */
export const stringToColor = (str) => {
    if (!str) return '#cccccc';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = hash % 360;
    return `hsl(${h}, 60%, 70%)`;
};

/**
 * Determines a contrasting text color (black or white) for a given background hex color.
 * @param {string} hexColor - The background color in hex format (e.g., '#RRGGBB').
 * @returns {string} The suggested text color ('#1f2937' for light bg, '#ffffff' for dark bg).
 */
export const getTextColorForBackground = (hexColor) => {
    if (!hexColor) return '#1f2937'; // gray-800 for light mode default
    
    // Check if color is HSL
    if (hexColor.startsWith('hsl')) {
        try {
            const l = parseInt(hexColor.match(/(\d+)%\)/)[1]);
            return l > 65 ? '#1f2937' : '#ffffff';
        } catch (e) {
            return '#1f2937';
        }
    }

    let color = hexColor.charAt(0) === '#' ? hexColor.substring(1, 7) : hexColor;
    if (color.length === 3) { color = color.split('').map(char => char + char).join(''); }
    const r = parseInt(color.substring(0, 2), 16), g = parseInt(color.substring(2, 4), 16), b = parseInt(color.substring(4, 6), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq >= 150 ? '#1f2937' : '#ffffff';
};
